import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext.hook';

/**
 * Registra acesso do usuário via Edge Function `log-access`.
 *
 * IP e user-agent são capturados server-side a partir dos headers da
 * requisição — não do browser — impedindo forjamento pelo client.
 * Dedupe (60s, mesma identidade user+ip+UA) é aplicado dentro da Edge Function.
 *
 * Em caso de falha (offline, 5xx, cold-start), o evento é enfileirado em
 * localStorage e re-tentado no próximo mount autenticado. Queue limitada
 * a 10 entradas para evitar crescimento descontrolado.
 */

const QUEUE_KEY = 'bravaforte:access-log-queue';
const MAX_QUEUE_SIZE = 10;

interface QueuedEntry {
  ts: number;
  userId: string;
}

function readQueue(): QueuedEntry[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QueuedEntry[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedEntry[]): void {
  try {
    if (queue.length === 0) {
      localStorage.removeItem(QUEUE_KEY);
      return;
    }
    // Mantém apenas os últimos N para evitar crescimento descontrolado.
    const trimmed = queue.slice(-MAX_QUEUE_SIZE);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
  } catch {
    /* localStorage indisponível (modo privado, quota): ignora silenciosamente. */
  }
}

async function flushQueue(): Promise<void> {
  try {
    const queue = readQueue();
    if (queue.length === 0) return;
    const { error } = await supabase.functions.invoke('log-access', {
      body: { retry: true },
    });
    if (!error) {
      // Sucesso: drena queue inteira (1 retry registra 1 row server-side com IP/UA atuais).
      writeQueue([]);
    }
  } catch {
    /* noop — preserva queue para próximo mount. */
  }
}

export function useAccessLogger() {
  const { user } = useAuth();
  const hasLogged = useRef(false);

  useEffect(() => {
    if (!user?.id || hasLogged.current) return;
    hasLogged.current = true;

    // 1) Tenta drenar eventos pendentes de mounts anteriores.
    void flushQueue();

    // 2) Registra o acesso atual; em falha, enfileira para retry.
    void supabase.functions
      .invoke('log-access', { body: {} })
      .then(({ error }) => {
        if (error) {
          const queue = readQueue();
          queue.push({ ts: Date.now(), userId: user.id });
          writeQueue(queue);
          if (import.meta.env.DEV) {
            console.warn('[access-logger] queued for retry (run `supabase functions serve` locally)', error);
          }
        }
      })
      .catch((err) => {
        const queue = readQueue();
        queue.push({ ts: Date.now(), userId: user.id });
        writeQueue(queue);
        if (import.meta.env.DEV) {
          console.warn('[access-logger] queued for retry (run `supabase functions serve` locally)', err);
        }
      });
  }, [user?.id]);
}
